
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {MarkupProfileDeleteResponse} from './MarkupProfileDeleteResponse';

/**
 * MarkupProfileDeleteRequest struct
 */
class MarkupProfileDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} markupProfileId
   * @param {string=} markupProfileName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), markupProfileId = null, markupProfileName = null) {
    super()
    super.copy(parent)
    this.MarkupProfileId = markupProfileId
    this.MarkupProfileName = markupProfileName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarkupProfileDeleteRequest}
   * @param {!MarkupProfileDeleteRequest} other Other struct
   * @returns {!MarkupProfileDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.MarkupProfileId != null) {
      this.MarkupProfileId = new UUID(other.MarkupProfileId)
    } else {
      this.MarkupProfileId = null
    }
    if (other.MarkupProfileName != null) {
      this.MarkupProfileName = other.MarkupProfileName
    } else {
      this.MarkupProfileName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarkupProfileDeleteRequest}
   * @returns {!MarkupProfileDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarkupProfileDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarkupProfileDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarkupProfileDeleteRequest}
   * @param {!MarkupProfileDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarkupProfileDeleteRequest)) {
      throw new TypeError('Instance of MarkupProfileDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarkupProfileDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      MarkupProfileId: ((this.MarkupProfileId != null) ? this.MarkupProfileId.toString() : null),
      MarkupProfileName: ((this.MarkupProfileName != null) ? this.MarkupProfileName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarkupProfileDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarkupProfileDeleteRequest} other Object value
   * @returns {!MarkupProfileDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new MarkupProfileDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarkupProfileDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarkupProfileDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarkupProfileDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6141
  }
}

export { MarkupProfileDeleteRequest };

/**
 * Fast Binary Encoding MarkupProfileDeleteRequest field model
 */
class FieldModelMarkupProfileDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.MarkupProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.MarkupProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.MarkupProfileId.fbeOffset + this.MarkupProfileId.fbeSize), buffer, this.MarkupProfileId.fbeOffset + this.MarkupProfileId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.MarkupProfileId.fbeSize + this.MarkupProfileName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.MarkupProfileId.fbeExtra + this.MarkupProfileName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarkupProfileDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6141
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.MarkupProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarkupProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileId.fbeSize

    if ((fbeCurrentSize + this.MarkupProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarkupProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {!MarkupProfileDeleteRequest} fbeValue Default value, defaults is new MarkupProfileDeleteRequest()
   * @returns {!MarkupProfileDeleteRequest} MarkupProfileDeleteRequest value
   */
  get (fbeValue = new MarkupProfileDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {!MarkupProfileDeleteRequest} fbeValue MarkupProfileDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.MarkupProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.MarkupProfileId = this.MarkupProfileId.get()
    } else {
      fbeValue.MarkupProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileId.fbeSize

    if ((fbeCurrentSize + this.MarkupProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.MarkupProfileName = this.MarkupProfileName.get()
    } else {
      fbeValue.MarkupProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarkupProfileName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {!MarkupProfileDeleteRequest} fbeValue MarkupProfileDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarkupProfileDeleteRequest}
   * @param {MarkupProfileDeleteRequest} fbeValue MarkupProfileDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.MarkupProfileId.set(fbeValue.MarkupProfileId)
    this.MarkupProfileName.set(fbeValue.MarkupProfileName)
  }
}

export { FieldModelMarkupProfileDeleteRequest };

/**
 * Fast Binary Encoding MarkupProfileDeleteRequest model
 */
class MarkupProfileDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarkupProfileDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the MarkupProfileDeleteRequest model
   * @this {!MarkupProfileDeleteRequestModel}
   * @returns {!FieldModelMarkupProfileDeleteRequest} model MarkupProfileDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarkupProfileDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarkupProfileDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarkupProfileDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarkupProfileDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarkupProfileDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarkupProfileDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarkupProfileDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarkupProfileDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarkupProfileDeleteRequestModel}
   * @param {!MarkupProfileDeleteRequest} value MarkupProfileDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarkupProfileDeleteRequestModel}
   * @param {!MarkupProfileDeleteRequest} value MarkupProfileDeleteRequest value, defaults is new MarkupProfileDeleteRequest()
   * @return {!object} Deserialized MarkupProfileDeleteRequest value and its size
   */
  deserialize (value = new MarkupProfileDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarkupProfileDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarkupProfileDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarkupProfileDeleteRequestModel}
   * @param {!number} prev Previous MarkupProfileDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarkupProfileDeleteRequestModel };
MarkupProfileDeleteRequest.__has_response = true;
MarkupProfileDeleteRequest.__response_class = MarkupProfileDeleteResponse;
